import { useEffect, useMemo, useRef, useState } from 'react'
import { Sunrise, Sunset, ChevronRight, X } from 'lucide-react'
import { useGlobeData, type GlobePoint } from './useGlobeData'
import { sunTimes, dayProgress, type SunTimes, type DayProgress } from './solar'
import { usePrefs, type ClockFormat } from '@/store/prefs'
import { fmtTimeClock } from '@/lib/time'
import { globeRampCss } from './ramp'

/** Shared state for any globe hero: live points + clock + sun + selection. */
export function useGlobeHero(location?: { lat: number; lon: number }) {
  const data = useGlobeData()
  const clock = usePrefs((s) => s.clock)
  const toggleClock = usePrefs((s) => s.toggleClock)
  const [now, setNow] = useState(() => new Date())
  const [selected, setSelected] = useState<GlobePoint | null>(null)

  // Tick clock + sun every 30s (cheap; the rAF loop drives the globe motion).
  useEffect(() => {
    const id = setInterval(() => setNow(new Date()), 30_000)
    return () => clearInterval(id)
  }, [])

  // Dismiss the tap sheet with Escape.
  useEffect(() => {
    if (!selected) return
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setSelected(null)
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [selected])

  const sun = useMemo<SunTimes | null>(
    () => (location ? sunTimes(now, location.lat, location.lon) : null),
    [location?.lat, location?.lon, now],
  )
  const prog = useMemo<DayProgress | null>(() => (sun ? dayProgress(now, sun) : null), [sun, now])

  return { ...data, clock, toggleClock, now, selected, setSelected, sun, prog }
}

/** Time-of-day greeting — both words title-cased. */
export function greetingFor(d: Date): string {
  const h = d.getHours()
  if (h < 5) return 'Good Night' // 12:00 am – 4:59 am
  if (h < 12) return 'Good Morning'
  if (h < 17) return 'Good Afternoon'
  return 'Good Evening'
}

export function ClockToggle({ clock, onToggle }: { clock: ClockFormat; onToggle: () => void }) {
  return (
    <button
      className="clock-toggle"
      onClick={onToggle}
      role="switch"
      aria-checked={clock === '24h'}
      aria-label="Toggle 24-hour time"
    >
      <span className={clock === '12h' ? 'is-on' : ''}>12h</span>
      <span className={clock === '24h' ? 'is-on' : ''}>24h</span>
    </button>
  )
}

export function PointSheet({
  point,
  onClose,
  onOpen,
}: {
  point: GlobePoint
  onClose: () => void
  onOpen: () => void
}) {
  const mainRef = useRef<HTMLButtonElement>(null)
  useEffect(() => {
    mainRef.current?.focus()
  }, [])
  return (
    <div
      className="point-sheet"
      role="dialog"
      aria-label={point.label}
      style={{ '--c': globeRampCss(point.level) } as React.CSSProperties}
    >
      <button ref={mainRef} className="point-sheet__main" onClick={onOpen}>
        <span className="point-sheet__dot" />
        <span className="point-sheet__text">
          <strong>{point.label}</strong>
          {point.detail && <span>{point.detail}</span>}
        </span>
        <ChevronRight size={18} className="point-sheet__chev" />
      </button>
      <button className="point-sheet__close" onClick={onClose} aria-label="Dismiss">
        <X size={16} />
      </button>
    </div>
  )
}

/** Self-contained sun-arc for a location — its own clock tick + 12h/24h pref. */
export function SunArcPanel({ location }: { location: { lat: number; lon: number } }) {
  const clock = usePrefs((s) => s.clock)
  const [now, setNow] = useState(() => new Date())
  useEffect(() => {
    const id = setInterval(() => setNow(new Date()), 30_000)
    return () => clearInterval(id)
  }, [])
  const sun = useMemo(() => sunTimes(now, location.lat, location.lon), [location.lat, location.lon, now])
  const prog = useMemo(() => dayProgress(now, sun), [sun, now])
  return <SunArc sun={sun} prog={prog} clock={clock} />
}

export function SunArc({ sun, prog, clock }: { sun: SunTimes; prog: DayProgress; clock: ClockFormat }) {
  // viewBox geometry (uniform-scaled via CSS width:100%; height auto).
  const W = 300
  const H = 58
  const x0 = 16
  const x1 = W - 16
  const baseY = 48
  const peak = 38
  const rx = (x1 - x0) / 2
  const f = prog.fraction
  const sunX = x0 + f * (x1 - x0)
  const sunY = baseY - peak * Math.sin(Math.PI * f)
  const track = `M ${x0} ${baseY} A ${rx} ${peak} 0 0 1 ${x1} ${baseY}`
  const travelled = `M ${x0} ${baseY} A ${rx} ${peak} 0 0 1 ${sunX} ${sunY}`
  const rise = sun.sunrise ? fmtTimeClock(sun.sunrise, clock) : sun.polar === 'day' ? 'Up all day' : '—'
  const set = sun.sunset ? fmtTimeClock(sun.sunset, clock) : sun.polar === 'night' ? 'Polar night' : '—'

  return (
    <div className="sun-arc">
      <svg className="sun-arc__svg" viewBox={`0 0 ${W} ${H}`} role="img" aria-label={`Sunrise ${rise}, sunset ${set}`}>
        <line x1={x0} y1={baseY} x2={x1} y2={baseY} className="sun-arc__horizon" />
        <path d={track} className="sun-arc__track" />
        {prog.isDay && <path d={travelled} className="sun-arc__progress" />}
        {prog.isDay && (
          <g className="sun-arc__sun" transform={`translate(${sunX} ${sunY})`}>
            <circle r="9" className="sun-arc__glow" />
            <circle r="4.2" className="sun-arc__disc" />
          </g>
        )}
      </svg>
      <div className="sun-arc__labels">
        <span className="sun-arc__end">
          <Sunrise size={15} />
          {rise}
        </span>
        <span className="sun-arc__end">
          {set}
          <Sunset size={15} />
        </span>
      </div>
    </div>
  )
}
